import React from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { FiPlus } from "react-icons/fi";
import { Nav, Tab } from "react-bootstrap";
import Layout from "components/Layout";
import MyDocuments from "components/Documents";
import DocumentsBulkList from "components/Documents/DocumentsBulkList";
import DocumentsAdd from "components/Documents/DocumentAddModal";
import Loading from "components/Loading";
import { showModal, hideModal } from "actions/modalActions";
import Modal from "components/Modal";
import { isAdmin, hasSubscriptionFeature } from "utils/utils";
import UpgradeModal from "components/Modal/UpgradeModal";
import { documentsService } from "services";
import DocumentsSignaturesBanner from "components/Documents/DocumentsSignaturesBanner";

class Documents extends React.Component {
	constructor(props) {
		super(props);

		this.closeModal = this.closeModal.bind(this);
		this.openAlertModal = this.openAlertModal.bind(this);
		this.openNewDocumentModal = this.openNewDocumentModal.bind(this);

		this.state = {
			loading: true,
			selectedTab: "mine",
			folders: [],
			showUpgradeModal: false,
			upgradeFeature: '',
		};
	}

	closeModal() {
		this.props.hideModal();
	}

	openAlertModal(message, title) {
		this.props.showModal(
			{
				open: true,
				title: title || "Error",
				style: { width: "400px" },
				message: message,
				closeModal: this.closeModal,
			},
			"alert"
		);
	}

	openNewDocumentModal() {
		const category = this.props.match.params.category;
		const tab = this.state.selectedTab;
		const { user } = this.props;

		this.props.showModal(
			{
				open: true,
				title:
					tab == "mine" ? this.props.t("documents.addDocuments") : this.props.t("documents.bulkUpload"),
				style: { width: "700px" },
				content: (
					<DocumentsAdd
						category={category}
						selectedTab={tab}
						employee={user}
						handleClose={this.closeModal}
						onDocumentAdded={this.handleDocumentAdded}
					/>
				),
				closeModal: this.closeModal,
			},
			"edit"
		);
	}

	// Función para manejar cuando se agrega un documento
	handleDocumentAdded = () => {
		const category = this.props.match.params.category;

		if (category) {
			this.refs.myDocuments?.componentDidReceiveRefresh();
		} else {
			this.refreshParentFolders();
		}
	}

	// Función para refrescar folders en el componente padre
	refreshParentFolders = () => {
		documentsService.getStoreFolders()
			.then(folders => {
				this.setState({ 
					folders: folders.data || []
				});
			})
			.catch(error => {
				console.error("❌ Error refreshing parent folders:", error);
			});
	}

	async componentDidMount() {
		const category = this.props.match.params.category;
		
		// Solo obtener la lista de folders si NO estamos en un folder específico
		if (!category) {
			documentsService.getStoreFolders()
				.then(folders => {
					this.setState({ 
						loading: false,
						folders: folders.data || []
					});
				})
				.catch(error => {
					console.error("Error fetching store folders:", error);
					this.setState({ loading: false });
				});
		} else {
			// Si estamos en un folder específico, no necesitamos cargar la lista
			this.setState({ loading: false });
		}
	}

	componentDidUpdate(prevProps) {
		// Detectar cuando el usuario navega de vuelta a /documents desde un folder específico
		const prevCategory = prevProps.match.params.category;
		const currentCategory = this.props.match.params.category;
		
		if (prevCategory && !currentCategory) {
			// El usuario navegó de vuelta a /documents desde un folder específico
			this.refreshParentFolders();
		}
	}

	selectTab = (tab) => {
		// Verificar si intenta acceder al tab de envío masivo sin suscripción
		if (tab === "bulk" && !hasSubscriptionFeature('MASS_SENDING_DOCUMENTS')) {
			this.setState({
				showUpgradeModal: true,
				upgradeFeature: this.props.t("documents.bulkTab")
			});
			// No cambiar el tab, mantener en "mine"
			return;
		}

		this.setState({
			selectedTab: tab,
		});
	};

	getEmployeeContent = () => {
		const category = this.props.match.params.category;
		const { folders } = this.state;
		
		// Si hay una categoría específica, mostrar directamente MyDocuments sin header
		if (category) {
			// No tenemos folders cuando estamos en un folder específico, pasar solo el category
			return <MyDocuments 
				ref="myDocuments" 
				category={category} 
				onRefresh={() => this.refs.myDocuments?.componentDidReceiveRefresh()}
			/>;
		}
		
		// Si no hay categoría, mostrar la vista normal con tabs
		return <MyDocuments 
			ref="myDocuments" 
			folders={folders} 
			onRefresh={() => this.refs.myDocuments?.componentDidReceiveRefresh()}
		/>;
	};

	getAdminContent = () => {
		const category = this.props.match.params.category;
		const { folders } = this.state;

		// Si hay una categoría específica, mostrar directamente MyDocuments sin tabs
		if (category) {
			// No tenemos folders cuando estamos en un folder específico, pasar solo el category
			return <MyDocuments 
				category={category} 
				onRefresh={this.handleDocumentAdded}
			/>;
		}

		// Si no hay categoría, mostrar la vista normal con tabs
		const hasMassSendingSubscription = hasSubscriptionFeature('MASS_SENDING_DOCUMENTS');
		
		return (
			<Tab.Container
				defaultActiveKey="mine"
				activeKey={this.state.selectedTab || "mine"}
				onSelect={(k) => this.selectTab(k)}
			>
				<Nav variant="tabs">
					<Nav.Item>
						<Nav.Link eventKey="mine">{this.props.t("documents.myDocuments")}</Nav.Link>
					</Nav.Item>
					<Nav.Item>
						<Nav.Link
							eventKey="bulk"
							onClick={(e) => {
								if (!hasMassSendingSubscription) {
									e.preventDefault();
									this.setState({
										showUpgradeModal: true,
										upgradeFeature: this.props.t("documents.bulkTab")
									});
									return false;
								}
							}}
							style={{
								opacity: !hasMassSendingSubscription ? 0.5 : 1,
								cursor: !hasMassSendingSubscription ? 'not-allowed' : 'pointer',
							}}
						>
							{this.props.t("documents.bulkTab")}
						</Nav.Link>
					</Nav.Item>
				</Nav>
				<Tab.Content>
					<Tab.Pane eventKey="mine">
						<MyDocuments 
							folders={folders} 
							onRefresh={this.handleDocumentAdded}
						/>
					</Tab.Pane>
					<Tab.Pane eventKey="bulk">
						{hasMassSendingSubscription ? (
							<DocumentsBulkList />
						) : (
							<div className="alert alert-info text-center" style={{ marginTop: '20px' }}>
								<p>{this.props.t("documents.bulkUpgradeMessage")}</p>
							</div>
						)}
					</Tab.Pane>
				</Tab.Content>
			</Tab.Container>
		);
	};

	getContent = () => {
		if (this.state.loading) {
			return <Loading />;
		}

		// TODO error control
		// if (!this.props.documents) {
		// 	return "Error cargando los documentos";
		// }

		// TODO first steps:
		// if (this.props.documents.length == 0) {
		// 	const step_text = isAdmin()
		// 		? "Desde aquí podrás aprobar o denegar las peticiones de ausencia de los empleados"
		// 		: "Desde aquí podrás solicitar vacaciones o ausencias para tu supervisor te las apruebe";

		// 	return (
		// 		<div className="first-steps">
		// 			<FiFolder className="icono-pasos" />
		// 			<h3 className="title2">Primeros pasos</h3>

		// 			<p>
		// 				{step_text}. Aquí te explicamos cómo funcionan:{" "}
		// 				<a
		// 					className="alink"
		// 					target="_blank"
		// 					href="https://help.plain.ninja/es/articles/3693962-que-es-una-ausencia"
		// 				>
		// 					https://help.plain.ninja/es/articles/3693962-que-es-una-ausencia
		// 				</a>
		// 			</p>
		// 		</div>
		// 	);
		// }

		return isAdmin() ? this.getAdminContent() : this.getEmployeeContent();
		// return this.getEmployeeContent();
	};

	render() {
		const category = this.props.match.params.category;
		
		return (
			<>
				<Layout className="page-documents" context="documents">
					{/* Solo mostrar el header si no hay categoría específica */}
					{!category && (
						<div className="heading">
							<h1 className="title">
								{isAdmin() ? this.props.t("documents.title") : this.props.t("documents.myDocuments")}
							</h1>
							<div className="heading-help-banner">
								<DocumentsSignaturesBanner />
							</div>

							{this.state.selectedTab == "mine" && <div className="heading-actions">
								<button
									type="button"
									title={this.props.t("documents.createDocument")}
									onClick={this.openNewDocumentModal}
									className="btn btn-new"
								>
									<FiPlus />
								</button>
							</div>}
						</div>
					)}

					{this.getContent()}
				</Layout>

				<Modal hideModal={this.props.hideModal} />

				<UpgradeModal
					show={this.state.showUpgradeModal}
					onClose={() => this.setState({ showUpgradeModal: false })}
					feature={this.state.upgradeFeature}
					requiredPlan="Pro"
				/>
			</>
		);
	}
}

const mapStateToProps = (reducers) => {
	return {
		// ...reducers.employeesReducer,
		user: reducers.authReducer.user,
		// loading: reducers.employeesReducer.loading
	};
};

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => {
		dispatch(showModal({ modalProps, modalType }));
	},
	//   getAllDocuments: () => dispatch(documentsActions.getAllDocuments()),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(Documents));
